/**
 * Tokens e boilerplate do design system.
 *
 * Fonte: `docs/design-system.md`, seções 2 a 6. O HTML público é autocontido: CSS e ícones são
 * embutidos, sem compiladores, fontes ou bibliotecas buscados na rede em runtime.
 */

import { CSS_TAILWIND } from './estilos-compilados.ts';
import { iconeEstatico } from './icones.ts';

export const CORES = {
  brandBlue: '#0A2540',
  brandLight: '#143C66',
  brandAccent: '#F97316',
} as const;

export const ASSETS = {
  logoEmaster:
    'https://emaster.cloud/wp-content/uploads/sites/10/2025/08/cropped-Logos-eMaster9.png',
  logoAws: 'https://committed.proposta.emaster.info/images/awslogo.webp',
  baseFotos: 'https://committed.proposta.emaster.info/inside_sales/',
} as const;

/** Marca imagens cujo erro deve ocultá-las sem atributo de evento inline. */
export const FALLBACK_IMAGEM = 'data-image-fallback' as const;

export const CONTAINER = 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8' as const;

const CSS_IMPRESSAO = `
        @media print {
            .no-print { display: none !important; }
            body { background-color: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            section { page-break-inside: avoid; break-inside: avoid; }
            .shadow-sm, .shadow-md, .shadow-lg, .shadow-xl, .shadow-2xl { box-shadow: none !important; }
            .tabela-recursos-cabecalho { display: table-header-group; }
            tr { page-break-inside: avoid; break-inside: avoid; }
            h2, h3 { page-break-after: avoid; break-after: avoid; }
            p { orphans: 3; widows: 3; }
            #investimento { page-break-before: always; break-before: page; }
            #contato { page-break-before: always; break-before: page; }
            img { max-height: 200px; }
            .diagrama-rolavel { overflow: visible !important; }
            .diagrama-rolavel > * { min-width: 0 !important; }
            @page { margin: 14mm 12mm; }
        }`;

const CSS_BASE = `
        .glass-card {
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(10px);
        }
        .hero-pattern {
            background-color: ${CORES.brandBlue};
            background-image: radial-gradient(circle at 100% 100%, ${CORES.brandLight} 0, ${CORES.brandBlue} 50%, ${CORES.brandBlue} 100%);
        }
        .grafico-distribuicao {
            display: flex;
            width: 100%;
            height: 2rem;
            overflow: hidden;
            border-radius: 9999px;
            background: #e5e7eb;
        }
        .grafico-distribuicao > span { min-width: 2px; }
        [data-image-fallback][hidden] { display: none !important; }`;

/** Nonce criptograficamente aleatório, diferente para cada HTML renderizado. */
export function criarNonceDocumento(): string {
  const bytes = new Uint8Array(18);
  globalThis.crypto.getRandomValues(bytes);
  return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Aceita endpoints relativos e HTTPS. Outros esquemas são desativados antes de chegarem ao HTML.
 */
export function normalizarUrlAceite(url: string | undefined): string | null {
  if (url === undefined || url.trim() === '') return null;

  try {
    const resolvida = new URL(url, 'https://documento.local');
    if (resolvida.protocol !== 'https:') return null;
    return url;
  } catch {
    return null;
  }
}

function connectSrc(urlAceite: string | null): string {
  if (urlAceite === null) return "'none'";
  const resolvida = new URL(urlAceite, 'https://documento.local');
  return resolvida.origin === 'https://documento.local' ? "'self'" : resolvida.origin;
}

function politicaConteudo(nonce: string, urlAceite: string | null): string {
  return [
    "default-src 'none'",
    `script-src 'nonce-${nonce}'`,
    "script-src-attr 'none'",
    // O CSS Tailwind é embutido, mas componentes legados e o diagrama SVG ainda usam style="".
    // A exceção é restrita a estilos; scripts inline só executam quando recebem o nonce aleatório.
    "style-src 'unsafe-inline'",
    'img-src https: data:',
    "font-src 'none'",
    `connect-src ${connectSrc(urlAceite)}`,
    "object-src 'none'",
    "base-uri 'none'",
    "form-action 'none'",
  ].join('; ');
}

/**
 * Monta o `<head>` do documento.
 *
 * @param titulo Já escapado pelo chamador.
 * @param descricao Já escapada. Usada em `meta description` e Open Graph.
 */
export function cabecalhoDocumento(
  titulo: string,
  descricao: string,
  nonce: string,
  urlAceite: string | null,
): string {
  return `<!DOCTYPE html>
<html lang="pt-BR" class="scroll-smooth">
<head>
    <meta charset="UTF-8">
    <meta http-equiv="Content-Security-Policy" content="${politicaConteudo(nonce, urlAceite)}">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta name="robots" content="noindex, nofollow">
    <link rel="icon" type="image/png" href="https://committed.proposta.emaster.info/images/emaster-favicon.png">
    <meta name="description" content="${descricao}">
    <meta property="og:title" content="${titulo}">
    <meta property="og:description" content="${descricao}">
    <meta property="og:type" content="website">
    <title>${titulo}</title>
    <style>${CSS_TAILWIND}
${CSS_BASE}
${CSS_IMPRESSAO}
    </style>
</head>
<body class="bg-gray-50 text-gray-800 antialiased selection:bg-brand-accent selection:text-white font-sans">`;
}

function scriptFallbackImagem(): string {
  return `
        document.querySelectorAll('img[data-image-fallback]').forEach(function (imagem) {
            function ocultar() { imagem.hidden = true; }
            imagem.addEventListener('error', ocultar);
            if (imagem.complete && imagem.naturalWidth === 0) ocultar();
        });`;
}

/** Fecha o documento e executa somente scripts próprios autorizados pelo nonce. */
export function rodapeDocumento(nonce: string, scriptsExtra: readonly string[] = []): string {
  const extras = scriptsExtra.filter((script) => script.trim() !== '').join('\n');
  return `
    <script nonce="${nonce}">${scriptFallbackImagem()}
${extras}
    </script>
</body>
</html>
`;
}

/** Ícone vetorial estático obtido de um mapa fechado, sem biblioteca em runtime. */
export function icone(nome: string, classe = 'w-5 h-5'): string {
  return iconeEstatico(nome, classe);
}
